/**
 * Generic money / date formatters shared across all apps (billing, admin, …).
 *
 * Money: cents → "$1,234.56" (USD symbol only; other currencies render no
 * symbol for now), or "—" when null/undefined.
 * Dates: ISO 8601 → "Apr 28, 2026" (and a date-time variant), or "—" when null.
 */
export const formatMoney = (cents, currency = 'usd') => {
	if (cents === null || cents === undefined) return '—';
	const value = Number(cents) / 100;
	const symbol = (currency || 'usd').toUpperCase() === 'USD' ? '$' : '';
	return `${symbol}${value.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
};

export const formatDate = (iso) => {
	if (!iso) return '—';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * An instant, rendered in a named zone when one is given and in the reader's
 * own zone when one is not.
 *
 * WHY A ZONE IS ACCEPTED AT ALL. Some documents are about a place rather than
 * about the reader: a school's ledger post-dates belong to the school's day,
 * not to the day the administrator reading them happens to be in. A payment
 * taken at 23:30 in Los Angeles on 31 August reads "Sep 1, 2:30 AM" to a
 * reader in New York, which places it outside a statement period the same page
 * prints as ending 31 August.
 *
 * `timeZone` is optional and the default is unchanged, so every existing caller
 * keeps the reader's zone — which is right for anything that really is about
 * the reader.
 *
 * AN UNUSABLE ZONE FALLS BACK RATHER THAN THROWING. The value arrives from an
 * API, which makes it a system boundary; `toLocaleDateString` answers an
 * unrecognised zone with a RangeError, and one bad field would otherwise take
 * the whole document down rather than one cell.
 *
 * @param {string|null|undefined} iso
 * @param {string} [timeZone] an IANA zone name, e.g. `America/Los_Angeles`.
 * @returns {string}
 */
export const formatDateTime = (iso, timeZone) => {
	if (!iso) return '—';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '—';
	const zone = timeZone ? { timeZone } : {};
	const render = (options) =>
		`${d.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			...options,
		})}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', ...options })}`;
	try {
		return render(zone);
	} catch {
		return render({});
	}
};
